/**
 * Input masks and formatting helpers (Brazilian document / phone patterns).
 */

export const MASKS = {
  CNPJ: 'AA.AAA.AAA/AAAA-99',
  CPF: '999.999.999-99',
  CEP: '99999-999',
  PHONE: '(99) 9999-9999',
  CELL: '(99) 99999-9999',
} as const;

/** Strip non-digits */
export function unmask(value: string): string {
  return (value || '').replace(/\D/g, '');
}

/** Strip mask from CPF/CNPJ keeping letters (alphanumeric CNPJ), uppercased */
export function unmaskDocument(value: string): string {
  return (value || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

/** CNPJ (numeric or alphanumeric): 12 chars [0-9A-Z] + 2 numeric check digits */
export function isCnpjFormat(value: string): boolean {
  return /^[0-9A-Z]{12}[0-9]{2}$/.test(unmaskDocument(value));
}

/** Validate CNPJ (numeric or alphanumeric) by format and check digits (value = char code - 48) */
export function isValidCnpj(value: string): boolean {
  const cnpj = unmaskDocument(value);
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj) || /^(.)\1+$/.test(cnpj)) return false;
  const checkDigit = (chars: string, weights: number[]): number => {
    const mod = weights.reduce((acc, w, i) => acc + (chars.charCodeAt(i) - 48) * w, 0) % 11;
    return mod < 2 ? 0 : 11 - mod;
  };
  const d1 = checkDigit(cnpj.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const d2 = checkDigit(cnpj.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return d1 === Number(cnpj[12]) && d2 === Number(cnpj[13]);
}

/** Format CNPJ: 00.000.000/0001-00 or 12.ABC.345/01DE-35 (letters only in the first 12 positions) */
export function formatCnpj(value: string): string {
  const chars = unmaskDocument(value)
    .split('')
    .filter((c, i) => i < 12 || /\d/.test(c))
    .join('')
    .slice(0, 14);
  return chars
    .replace(/^(.{2})(.)/, '$1.$2')
    .replace(/^(.{2}\.)(.{3})(.)/, '$1$2.$3')
    .replace(/^(.{2}\..{3}\.)(.{3})(.)/, '$1$2/$3')
    .replace(/^(.{2}\..{3}\..{3}\/)(.{4})(.)/, '$1$2-$3');
}

/** Format CPF: 000.000.000-00 */
export function formatCpf(value: string): string {
  const digits = unmask(value).slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1-$2');
}

/** Format CNPJ or CPF: letters or more than 11 characters mean CNPJ */
export function formatCnpjCpf(value: string): string {
  const clean = unmaskDocument(value);
  if (clean.length <= 11 && !/[A-Z]/.test(clean)) return formatCpf(value);
  return formatCnpj(value);
}

/** Format CEP: 00000-000 */
export function formatCep(value: string): string {
  const digits = unmask(value).slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
}

/** Format phone: (11) 3333-4444 or (11) 99999-9999 */
export function formatPhone(value: string): string {
  const digits = unmask(value);

  if (digits.length > 11) return digits;

  if (digits.length <= 2) return digits ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

/** True when value looks like CNPJ (12+ characters) */
export function isCnpj(value: string): boolean {
  return unmaskDocument(value).length >= 12;
}
